import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { ThumbsUp, Minus, ThumbsDown, TrendingUp, BarChart3 } from 'lucide-react';
import { dividendService } from '../../Services/api/dividendService';

const StockAnalysisCard = ({ stockSymbol, industry, days }) => {
  // Dividend States
  const [dividendData, setDividendData] = useState({
    dividend_amount: "Does not pay Dividend",
    dividend_yield: null,
    last_ex_date: null,
    last_pay_date: null
  });
  const [dividendLoading, setDividendLoading] = useState(true);
  const [dividendError, setDividendError] = useState(null);

  // Sentiment States
  const [sentimentData, setSentimentData] = useState({});
  const [sentimentLoading, setSentimentLoading] = useState(true);

  // Fetch Dividend Data
  useEffect(() => {
    const fetchDividendData = async () => {
      try {
        setDividendLoading(true);
        const data = await dividendService.fetchDividendData(stockSymbol);
        setDividendData(data);
      } catch (err) {
        setDividendError(err.message);
      } finally {
        setDividendLoading(false);
      }
    };

    if (stockSymbol) {
      fetchDividendData();
    }
  }, [stockSymbol]);

  // Fetch Sentiment Data
  useEffect(() => {
    const fetchSentimentData = async () => {
      setSentimentLoading(true);
      try {
        const response = await fetch(
          process.env.REACT_APP_BACKEND_BASE_URL +  `/api/sentiment?industry=${industry}&days=${days}`
        );
        const jsonData = await response.json();
        setSentimentData(jsonData);
      } catch (error) {
        setSentimentData({
          "prosusAI": {
            "positive": 0,
            "neutral": 5,
            "negative": 1,
            "positive_percentage": 0.0,
            "neutral_percentage": 83.33333333333334,
            "negative_percentage": 16.666666666666664,
          }
        });
      } finally {
        setSentimentLoading(false);
      }
    };

    fetchSentimentData();
  }, [industry, days]);

  const formatPercentage = (value) => {
    return typeof value === 'number' ? value.toFixed(1) : '0.0';
  };

  const formatModelName = (modelKey) => {
    // Convert camelCase to readable format
    return modelKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  const getSentimentIcon = (type) => {
    switch (type) {
      case 'positive': return ThumbsUp;
      case 'neutral': return Minus;
      case 'negative': return ThumbsDown;
      default: return BarChart3;
    }
  };

  const getSentimentColors = (type) => {
    switch (type) {
      case 'positive': 
        return {
          color: 'text-emerald-600',
          bgColor: 'bg-gradient-to-br from-emerald-50 to-green-100',
          borderColor: 'border-emerald-200',
          ringColor: 'ring-emerald-200'
        };
      case 'neutral': 
        return {
          color: 'text-slate-600',
          bgColor: 'bg-gradient-to-br from-slate-50 to-blue-100',
          borderColor: 'border-slate-200',
          ringColor: 'ring-slate-200'
        };
      case 'negative': 
        return {
          color: 'text-rose-600',
          bgColor: 'bg-gradient-to-br from-rose-50 to-red-100',
          borderColor: 'border-rose-200',
          ringColor: 'ring-rose-200'
        };
      default: 
        return {
          color: 'text-gray-600',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-200',
          ringColor: 'ring-gray-200'
        };
    }
  };

  return (
    <Card className="w-full max-w-full mx-auto lg:ml-0 md:ml-0 sm:ml-0 shadow-lg border-0 bg-gradient-to-br from-white to-gray-50">
      <CardHeader className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-t-lg">
        <CardTitle className="text-lg sm:text-xl font-bold flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Stock Analysis: {stockSymbol}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <div className="space-y-6">
          {/* Dividend Section - Full Width on Top */}
          <div className="w-full">
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
              <div className="w-2 h-6 bg-gradient-to-b from-blue-500 to-purple-500 rounded-full"></div>
              Dividend Information
            </h3>
            {dividendLoading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            ) : dividendError ? (
              <div className="text-red-500 text-sm md:text-base">Error: {dividendError}</div>
            ) : dividendService.isDescriptiveResponse(dividendData.dividend_amount) ? (
              <div className="p-6 bg-gradient-to-br from-amber-50 to-yellow-100 rounded-xl border border-amber-200 text-center shadow-lg">
                <div className="text-lg font-semibold text-amber-800 mb-2">Dividend Status</div>
                <div className="text-amber-700">{dividendData.dividend_amount}</div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden">
                <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
                  <h4 className="font-bold text-gray-800 text-center">Dividend Details</h4>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {Object.entries(dividendData).map(([key, value]) => (
                      <div key={key} className="flex flex-col items-center p-4 bg-gradient-to-r from-gray-50 to-slate-100 rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200 text-center">
                        <span className="font-semibold text-gray-700 text-sm mb-2">
                          {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </span>
                        <span className="text-gray-600 font-bold text-lg">{value || "N/A"}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sentiment Section - Side by Side */}
          <div>
            <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2 flex-wrap mb-4">
              <div className="w-2 h-6 bg-gradient-to-b from-green-500 to-blue-500 rounded-full"></div>
              <span>Sentiment Analysis for {industry}</span>
              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                Last {days} days
              </span>
            </h3>
            {sentimentLoading ? (
              <div className="animate-pulse grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="h-48 bg-gray-200 rounded-xl"></div>
                <div className="h-48 bg-gray-200 rounded-xl"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Object.entries(sentimentData).map(([modelKey, modelData]) => (
                  <div key={modelKey} className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300">
                    <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-t-xl">
                      <h4 className="font-bold text-gray-800 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 text-indigo-600" />
                        {formatModelName(modelKey)}
                      </h4>
                    </div>
                    <div className="p-4">
                      <div className="grid gap-3">
                        {['positive', 'neutral', 'negative'].map((sentimentType) => {
                          const Icon = getSentimentIcon(sentimentType);
                          const colors = getSentimentColors(sentimentType);
                          const count = modelData[sentimentType] || 0;
                          const percentage = formatPercentage(modelData[`${sentimentType}_percentage`]);
                          
                          return (
                            <div
                              key={sentimentType}
                              className={`flex items-center justify-between p-3 rounded-xl border-2 ${colors.borderColor} ${colors.bgColor} hover:shadow-md hover:scale-105 transition-all duration-200`}
                            >
                              <div className="flex items-center space-x-2">
                                <div className={`p-1.5 rounded-full bg-white shadow-sm ${colors.ringColor} ring-2`}>
                                  <Icon className={`w-3 h-3 ${colors.color}`} />
                                </div>
                                <span className="font-bold text-gray-800 capitalize text-sm">
                                  {sentimentType}
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="text-right">
                                  <div className="font-bold text-gray-800 text-sm">{count}</div>
                                  <div className="text-xs text-gray-500">articles</div>
                                </div>
                                <div className={`${colors.color} font-bold text-sm px-2 py-1 bg-white rounded-lg shadow-sm`}>
                                  {percentage}%
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StockAnalysisCard;